import type { EpicAssessmentSummary, EpicWithCalculations } from '../api/shared/types';

export function mapSummaryToEpicWithCalculations(summary: EpicAssessmentSummary): EpicWithCalculations {
  const hasOverride = summary.override !== null;
  const overrideSelectedPct = summary.override?.selected_percentage ?? '';

  let activeCapExPercentage = summary.capex_percentage_ai ?? 0;
  let activeRnDPercentage = summary.rnd_percentage_ai ?? 0;
  let capExSource: 'M' | 'C' | 'A' = 'C';
  let rndSource: 'M' | 'C' | 'A' = 'C';

  // Use separate capex_source / rnd_source fields when available (new schema),
  // falling back to the legacy selected_percentage for backward compatibility.
  const capexSrc = hasOverride ? (summary.override!.capex_source ?? overrideSelectedPct) : null;
  const rndSrc = hasOverride ? (summary.override!.rnd_source ?? overrideSelectedPct) : null;

  // Resolve CapEx source
  if (capexSrc === 'manual') {
    // original_*_percentage and manual_*_percentage can be null at runtime (backend DTO is nullable); coalesce mirrors backend ResolveAxisPercentage contract
    activeCapExPercentage = summary.override!.manual_capex_percentage ?? summary.capex_percentage_ai ?? 0;
    capExSource = 'M';
  } else if (capexSrc === 'alm' || capexSrc === 'original') {
    // original_*_percentage and manual_*_percentage can be null at runtime (backend DTO is nullable); coalesce mirrors backend ResolveAxisPercentage contract
    activeCapExPercentage = summary.original_capex_percentage ?? summary.capex_percentage_ai ?? 0;
    capExSource = 'A';
  } else if ((summary.original_capex_percentage ?? 0) > (summary.capex_percentage_ai ?? 0)) {
    activeCapExPercentage = summary.original_capex_percentage ?? 0;
    capExSource = 'A';
  }

  // Resolve R&D source
  if (rndSrc === 'manual') {
    // original_*_percentage and manual_*_percentage can be null at runtime (backend DTO is nullable); coalesce mirrors backend ResolveAxisPercentage contract
    activeRnDPercentage = summary.override!.manual_rnd_percentage ?? summary.rnd_percentage_ai ?? 0;
    rndSource = 'M';
  } else if (rndSrc === 'alm' || rndSrc === 'original') {
    // original_*_percentage and manual_*_percentage can be null at runtime (backend DTO is nullable); coalesce mirrors backend ResolveAxisPercentage contract
    activeRnDPercentage = summary.original_rnd_percentage ?? summary.rnd_percentage_ai ?? 0;
    rndSource = 'A';
  } else if ((summary.original_rnd_percentage ?? 0) > (summary.rnd_percentage_ai ?? 0)) {
    activeRnDPercentage = summary.original_rnd_percentage ?? 0;
    rndSource = 'A';
  }

  const durationDays = summary.metadata.duration_created_to_closed || 0;
  const defaultEstimate = durationDays || 1;
  const hasManualEstimate = hasOverride && summary.override!.epic_size === 'manual'
    && summary.override!.manual_estimate_value != null && summary.override!.manual_estimate_value > 0;
  const estimate = hasManualEstimate
    ? summary.override!.manual_estimate_value!
    : defaultEstimate;
  const estimateSource: 'D' | 'M' =
    hasManualEstimate ? 'M' : 'D';

  return {
    id: summary.epic_id,
    title: summary.metadata.title,
    story_points: summary.metadata.story_points,
    effort: null,
    original_estimate: null,
    active_date: summary.metadata.active_date || null,
    closed_date: summary.metadata.closed_date || null,
    capex_percentage: summary.original_capex_percentage,
    r_and_d_percentage: summary.original_rnd_percentage,
    data_source: null,
    estimate,
    estimateSource,
    durationDays,
    activeCapExPercentage,
    activeRnDPercentage,
    capExSource,
    rndSource,
    claudeCapExPercentage: summary.capex_percentage_ai ?? 0,
    claudeRnDPercentage: summary.rnd_percentage_ai ?? 0,
    activeEstimate: estimate,
    estimateOverrideSource: hasManualEstimate ? 'M' : null,
    project_name: summary.metadata.project_name,
    fiscal_year_quarter: summary.metadata.fiscal_year_quarter,
  };
}
